import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, hasCapability, hasRole } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Storefront from "@/pages/Storefront";
import StorefrontHome from "@/pages/StorefrontHome";
import StorefrontProduct from "@/pages/StorefrontProduct";
import StorefrontCombo from "@/pages/StorefrontCombo";
import Checkout from "@/pages/Checkout";
import CheckoutResult from "@/pages/CheckoutResult";
import InvestmentLanding from "@/pages/InvestmentLanding";
import CommissionLanding from "@/pages/CommissionLanding";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ProductsCompare from "@/pages/ProductsCompare";
import LotPublic from "@/pages/LotPublic";
import Movements from "@/pages/Movements";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Banks from "@/pages/Banks";
import Finance from "@/pages/Finance";
import Metrics from "@/pages/Metrics";
import FinanceBreakdown from "@/pages/FinanceBreakdown";
import MonthlyReport from "@/pages/MonthlyReport";
import Expenses from "@/pages/Expenses";
import ExpenseRegister from "@/pages/ExpenseRegister";
import Clients from "@/pages/Clients";
import CRM from "@/pages/CRM";
import Sales from "@/pages/Sales";
import Marketing from "@/pages/Marketing";
import Publications from "@/pages/Publications";
import PublicationsOverview from "@/pages/PublicationsOverview";
import { PortalLogin, PortalApp } from "@/pages/Portal";
import PortalOrders from "@/pages/PortalOrders";
import OnlineOrders from "@/pages/OnlineOrders";
import Coupons from "@/pages/Coupons";
import Promotions from "@/pages/Promotions";
import Combos from "@/pages/Combos";
import Groupings from "@/pages/Groupings";
import PromotionsAnalytics from "@/pages/PromotionsAnalytics";
import PriceResearch from "@/pages/PriceResearch";
import AlendraInsights from "@/pages/AlendraInsights";
import MyTasks from "@/pages/MyTasks";
import HrEmployees from "@/pages/HrEmployees";
import HrClockKiosk from "@/pages/HrClockKiosk";
import HrWeeklyReport from "@/pages/HrWeeklyReport";
import HrAuthorizations from "@/pages/HrAuthorizations";
import RecurringExpenses from "@/pages/RecurringExpenses";
import Hospital from "@/pages/Hospital";
import Patients from "@/pages/Patients";
import Preinventario from "@/pages/Preinventario";
import MyTechWork from "@/pages/MyTechWork";
import Ads from "@/pages/Ads";
import Banners from "@/pages/Banners";
import ForcePasswordChange from "@/components/ForcePasswordChange";

function Protected({ children, roles, capabilities }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles || capabilities) {
    const roleOk = roles && roles.some((r) => hasRole(user, r));
    const capOk = capabilities && capabilities.some((c) => hasCapability(user, c));
    if (!roleOk && !capOk) return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function Root() {
  const { user } = useAuth();
  if (user?.must_change_password) return <ForcePasswordChange />;
  // Marketing-only users land directly on their workspace
  const roles = user?.roles || (user?.role ? [user.role] : []);
  const isMarketingOnly = roles.length === 1 && roles[0] === "marketing";
  if (isMarketingOnly && window.location.pathname === "/dashboard") {
    return <Navigate to="/marketing" replace />;
  }
  return <Layout />;
}

// Legacy `/tienda/p/:id` and `/tienda/combo/:id` redirect to the new
// `/catalogo/*` canonical routes to avoid breaking QRs and shared links.
function StorefrontProductRedirect() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const q = sp.toString();
  return <Navigate to={`/catalogo/p/${id}${q ? `?${q}` : ""}`} replace />;
}

function StorefrontComboRedirect() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const q = sp.toString();
  return <Navigate to={`/catalogo/combo/${id}${q ? `?${q}` : ""}`} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
        <Routes>
          {/* Root: nuevo home de tienda enriquecida (public storefront) */}
          <Route path="/" element={<StorefrontHome />} />
          {/* Landing corporativo del SaaS movido a /empresa por si se necesita */}
          <Route path="/empresa" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          {/* Catálogo público (URL antigua /tienda mantenida como alias con
              redirect para no romper QRs/links compartidos previos). Cuando
              el nuevo home de la tienda esté listo, /tienda apuntará a la
              nueva UI enriquecida y /catalogo continuará con esta vista. */}
          <Route path="/catalogo" element={<Storefront />} />
          <Route path="/catalogo/p/:id" element={<StorefrontProduct />} />
          <Route path="/catalogo/combo/:id" element={<StorefrontCombo />} />
          {/* Nuevo home enriquecido (solo productos con show_in_storefront=true). */}
          <Route path="/tienda" element={<StorefrontHome />} />
          <Route path="/tienda/p/:id" element={<StorefrontProductRedirect />} />
          <Route path="/tienda/combo/:id" element={<StorefrontComboRedirect />} />
          <Route path="/lote/:token" element={<LotPublic />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutResult />} />
          <Route path="/checkout/pending" element={<CheckoutResult />} />
          <Route path="/checkout/failure" element={<CheckoutResult />} />
          <Route path="/esquemadeinversion" element={<InvestmentLanding />} />
          <Route path="/esquemadecomisiones" element={<CommissionLanding />} />
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal" element={<PortalApp />} />
          <Route path="/reloj-checador" element={<HrClockKiosk />} />
          <Route element={<Protected><Root /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/marketing" element={
              <Protected roles={["super_admin","inventory","marketing"]}><Marketing /></Protected>
            } />
            <Route path="/products" element={
              <Protected roles={["super_admin","inventory","sales"]}><Products /></Protected>
            } />
            <Route path="/products/compare" element={
              <Protected roles={["super_admin","inventory"]}><ProductsCompare /></Protected>
            } />
            <Route path="/movements" element={
              <Protected roles={["super_admin","inventory"]}><Movements /></Protected>
            } />
            <Route path="/reports" element={
              <Protected roles={["super_admin"]}><Reports /></Protected>
            } />
            <Route path="/settings" element={
              <Protected roles={["super_admin","inventory"]}><Settings /></Protected>
            } />
            <Route path="/users" element={
              <Protected roles={["super_admin"]}><Users /></Protected>
            } />
            <Route path="/banks" element={
              <Protected roles={["super_admin"]}><Banks /></Protected>
            } />
            <Route path="/finance" element={
              <Protected roles={["super_admin"]}><Finance /></Protected>
            } />
            <Route path="/metricos" element={
              <Protected roles={["super_admin"]}><Metrics /></Protected>
            } />
            <Route path="/reporte-mensual" element={
              <Protected roles={["super_admin"]}><MonthlyReport /></Protected>
            } />
            <Route path="/finance/income" element={
              <Protected roles={["super_admin"]}><FinanceBreakdown type="income" /></Protected>
            } />
            <Route path="/finance/expense" element={
              <Protected roles={["super_admin"]}><FinanceBreakdown type="expense" /></Protected>
            } />
            <Route path="/expenses" element={
              <Protected roles={["super_admin"]}><Expenses /></Protected>
            } />
            <Route path="/expenses/new" element={
              <Protected roles={["super_admin"]} capabilities={["add_expenses"]}><ExpenseRegister /></Protected>
            } />
            <Route path="/clients" element={
              <Protected roles={["super_admin","sales"]}><Clients /></Protected>
            } />
            <Route path="/crm" element={
              <Protected roles={["super_admin","sales"]}><CRM /></Protected>
            } />
            <Route path="/sales" element={
              <Protected roles={["super_admin","sales"]}><Sales /></Protected>
            } />
            <Route path="/portal-orders" element={
              <Protected roles={["super_admin","sales"]}><PortalOrders /></Protected>
            } />
            <Route path="/online-orders" element={
              <Protected roles={["super_admin"]} capabilities={["manage_online_orders"]}><OnlineOrders /></Protected>
            } />
            <Route path="/coupons" element={
              <Protected roles={["super_admin"]}><Coupons /></Protected>
            } />
            <Route path="/promotions" element={
              <Protected roles={["super_admin"]}><Promotions /></Protected>
            } />
            <Route path="/combos" element={
              <Protected roles={["super_admin"]}><Combos /></Protected>
            } />
            <Route path="/groupings" element={
              <Protected roles={["super_admin"]}><Groupings /></Protected>
            } />
            <Route path="/promotions/analytics" element={
              <Protected roles={["super_admin"]}><PromotionsAnalytics /></Protected>
            } />
            <Route path="/research" element={
              <Protected roles={["super_admin"]} capabilities={["manage_price_research"]}><PriceResearch /></Protected>
            } />
            <Route path="/insights" element={
              <Protected roles={["super_admin"]}><AlendraInsights /></Protected>
            } />
            <Route path="/mis-tareas" element={
              <Protected><MyTasks /></Protected>
            } />
            <Route path="/publications" element={
              <Protected roles={["super_admin","sales"]}><Publications /></Protected>
            } />
            <Route path="/publications-overview" element={
              <Protected roles={["super_admin"]}><PublicationsOverview /></Protected>
            } />
            <Route path="/hr/empleados" element={
              <Protected roles={["super_admin"]}><HrEmployees /></Protected>
            } />
            <Route path="/hr/reporte-semanal" element={
              <Protected roles={["super_admin"]}><HrWeeklyReport /></Protected>
            } />
            <Route path="/hr/autorizaciones" element={
              <Protected roles={["super_admin"]}><HrAuthorizations /></Protected>
            } />
            <Route path="/gastos-recurrentes" element={
              <Protected roles={["super_admin"]}><RecurringExpenses /></Protected>
            } />
            <Route path="/hospital" element={
              <Protected roles={["super_admin", "inventory_admin", "sales", "sales_supervisor", "inventory", "technician", "taller"]}><Hospital /></Protected>
            } />
            <Route path="/hospital-equipos" element={
              <Protected roles={["super_admin", "inventory", "technician", "taller"]}><Patients /></Protected>
            } />
            <Route path="/preinventario" element={
              <Protected roles={["super_admin", "inventory"]} capabilities={["manage_preinventory"]}><Preinventario /></Protected>
            } />
            <Route path="/mi-tablero" element={
              <Protected roles={["super_admin", "inventory", "technician", "taller"]}><MyTechWork /></Protected>
            } />
            <Route path="/ads" element={
              <Protected roles={["super_admin", "marketing"]}><Ads /></Protected>
            } />
            <Route path="/banners" element={
              <Protected roles={["super_admin"]}><Banners /></Protected>
            } />
          </Route>
        </Routes>
      </BrowserRouter>
      </CartProvider>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}
